import React, { useEffect } from 'react';
import { ArrowLeft, ArrowRight, ArrowUpRight, CheckCircle2, Clock, Layers, Tag } from 'lucide-react';
import { Reveal, RevealGroup, RevealItem } from './ui/Reveal';
import { BrandMark } from './ui/BrandLogos';
import VideoPlayer from './ui/VideoPlayer';
import { findCaseStudy, nextCaseStudy } from '../lib/caseStudies';
import { routeHref } from '../lib/router';
import { calendlyLinkProps } from '../lib/calendly';

const NOT_FOUND = (
    <section className="container-x min-h-[70vh] flex flex-col items-center justify-center text-center pt-32 pb-24">
        <h1 className="font-display text-4xl font-bold text-white">Project not found</h1>
        <p className="mt-4 text-text-muted">That case study doesn't exist — it may have been renamed.</p>
        <a href="#work" className="btn-primary mt-8">
            Back to selected work
            <ArrowRight className="w-4 h-4" />
        </a>
    </section>
);

const CaseStudyPage: React.FC<{ slug: string }> = ({ slug }) => {
    const study = findCaseStudy(slug);

    // Keep the tab title and share description in step with the page being viewed.
    useEffect(() => {
        if (!study) return;
        const previousTitle = document.title;
        document.title = `${study.title} — AutomateIT.ng`;
        const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
        const previousDescription = meta?.content;
        if (meta) meta.content = study.summary;
        return () => {
            document.title = previousTitle;
            if (meta && previousDescription) meta.content = previousDescription;
        };
    }, [study]);

    if (!study) return NOT_FOUND;

    const next = nextCaseStudy(study.slug);

    return (
        <article className="relative pt-28 sm:pt-32 pb-24">
            {/* ambient glow, matching the hero treatment */}
            <div className="absolute inset-x-0 top-0 h-[520px] pointer-events-none overflow-hidden">
                <div className="absolute top-[-25%] left-1/2 -translate-x-1/2 w-[900px] h-[560px] bg-brand-blue/20 rounded-full blur-[140px] opacity-40" />
            </div>

            <div className="container-x relative">
                <a
                    href="#work"
                    className="inline-flex items-center gap-2 text-sm font-medium text-text-muted hover:text-brand-cyan transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    All work
                </a>

                {/* Title block */}
                <Reveal className="mt-8 max-w-3xl">
                    <span className="eyebrow mb-4">
                        <span className="h-px w-6 bg-brand-cyan/60" />
                        {study.industry}
                    </span>
                    <h1 className="font-display text-[2.25rem] leading-[1.1] sm:text-5xl lg:text-[3.5rem] font-bold text-white tracking-tight text-balance">
                        {study.title}
                    </h1>
                    <p className="mt-6 text-lg md:text-xl text-text-muted leading-relaxed">{study.summary}</p>
                </Reveal>

                {/* Meta strip */}
                <Reveal delay={0.08} className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
                    <span className="inline-flex items-center gap-2 text-text-muted">
                        <Clock className="w-4 h-4 text-brand-cyan" />
                        {study.timeline}
                    </span>
                    <span className="inline-flex items-center gap-2 text-text-muted">
                        <Layers className="w-4 h-4 text-brand-cyan" />
                        {study.services.join(' · ')}
                    </span>
                    <span className="inline-flex items-center gap-2 text-text-muted">
                        <Tag className="w-4 h-4 text-brand-cyan" />
                        {study.tags.join(', ')}
                    </span>
                </Reveal>

                {/* Cover */}
                <Reveal delay={0.12} className="mt-12">
                    <div className="relative overflow-hidden rounded-2xl card-surface">
                        <img src={study.image} alt={study.title} className="w-full aspect-[16/9] object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 to-transparent pointer-events-none" />
                    </div>
                </Reveal>

                {/* Results band */}
                <RevealGroup className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4" stagger={0.06}>
                    {study.results.map((result) => (
                        <RevealItem key={result.label}>
                            <div className="h-full rounded-2xl card-surface p-6">
                                <div className="font-display text-3xl md:text-4xl font-bold text-gradient leading-none">
                                    {result.value}
                                </div>
                                <div className="mt-2.5 text-sm text-text-muted leading-snug">{result.label}</div>
                            </div>
                        </RevealItem>
                    ))}
                </RevealGroup>

                {/* Narrative */}
                <div className="mt-20 grid lg:grid-cols-[1fr_20rem] gap-12 lg:gap-16 items-start">
                    <div className="min-w-0">
                        <Reveal>
                            <h2 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight">
                                The challenge
                            </h2>
                            <div className="mt-5 space-y-4">
                                {study.challenge.map((paragraph) => (
                                    <p key={paragraph.slice(0, 40)} className="text-text-muted text-lg leading-relaxed">
                                        {paragraph}
                                    </p>
                                ))}
                            </div>
                        </Reveal>

                        <Reveal className="mt-16">
                            <h2 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight">
                                How we built it
                            </h2>
                        </Reveal>

                        <RevealGroup className="mt-8 space-y-4" stagger={0.06}>
                            {study.approach.map((step, i) => (
                                <RevealItem key={step.title}>
                                    <div className="flex gap-4 rounded-2xl card-surface p-6">
                                        <span className="grid place-items-center w-8 h-8 shrink-0 rounded-lg bg-brand-cyan/10 ring-1 ring-brand-cyan/30 font-mono text-sm font-semibold text-brand-cyan">
                                            {i + 1}
                                        </span>
                                        <div className="min-w-0">
                                            <h3 className="font-display text-lg font-bold text-white">{step.title}</h3>
                                            <p className="mt-2 text-text-muted leading-relaxed">{step.body}</p>
                                        </div>
                                    </div>
                                </RevealItem>
                            ))}
                        </RevealGroup>
                    </div>

                    {/* Sidebar */}
                    <Reveal delay={0.1} className="lg:sticky lg:top-28">
                        <div className="rounded-2xl card-surface p-6">
                            <h3 className="font-display font-bold text-white">Built with</h3>
                            <ul className="mt-4 space-y-3">
                                {study.stack.map((tool) => (
                                    <li key={tool.label} className="flex items-center gap-3 text-sm text-text-main">
                                        {tool.brand ? (
                                            <BrandMark brand={tool.brand} className="w-[18px] h-[18px] shrink-0" />
                                        ) : (
                                            <CheckCircle2 className="w-[18px] h-[18px] shrink-0 text-brand-cyan/70" />
                                        )}
                                        {tool.label}
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-6 pt-6 border-t border-white/[0.06]">
                                <p className="text-sm text-text-muted leading-relaxed">
                                    Running something similar by hand? We'll map it and tell you what's automatable —
                                    free.
                                </p>
                                <a {...calendlyLinkProps} className="btn-primary w-full mt-5">
                                    Book an audit
                                    <ArrowRight className="w-4 h-4" />
                                </a>
                            </div>
                        </div>
                    </Reveal>
                </div>

                {/* Optional walkthrough video */}
                {study.video && (
                    <Reveal className="mt-20 max-w-4xl">
                        <h2 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight mb-6">
                            See it running
                        </h2>
                        <VideoPlayer url={study.video} title={`${study.title} walkthrough`} />
                    </Reveal>
                )}

                {/* Next project */}
                <Reveal className="mt-20">
                    <a
                        href={routeHref(`/work/${next.slug}`)}
                        className="group flex flex-col sm:flex-row sm:items-center gap-6 rounded-2xl card-surface p-6 hover:border-white/[0.14] transition-colors"
                    >
                        <img
                            src={next.image}
                            alt=""
                            className="w-full sm:w-40 h-32 sm:h-24 object-cover rounded-xl shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                            <div className="text-xs uppercase tracking-[0.2em] text-text-faint font-semibold">
                                Next project
                            </div>
                            <div className="mt-2 font-display text-xl font-bold text-white group-hover:text-brand-cyan transition-colors">
                                {next.title}
                            </div>
                            <p className="mt-1 text-sm text-text-muted line-clamp-2">{next.description}</p>
                        </div>
                        <ArrowUpRight className="w-6 h-6 text-text-muted group-hover:text-brand-cyan transition-colors shrink-0 hidden sm:block" />
                    </a>
                </Reveal>
            </div>
        </article>
    );
};

export default CaseStudyPage;
